import * as React from 'react';
import {Box, Divider, Grid, Typography} from '@material-ui/core';
import {Field} from 'formik';
import {Duration} from 'components/Duration';
import moment from 'moment';
import {TextField} from 'formik-material-ui';
import {ButtonBar} from 'components/ButtonBar';
import {useHistory, useParams} from 'react-router';
import {BaseForm} from 'components/BaseForm';
import {useEngineContext} from 'contexts/EngineContext';
import * as yup from 'yup';
import {StartEndDateTime} from '@deckmans/web-shared';
import {useShiploader} from 'hooks/useShiploader';
import {useVessel, useVesselActivity} from 'hooks/useVessel';
import {getLastVesselActivityEndDate} from '@deckmans/domain/lib/util/sortVesselActivities';
import {useHistoryStateDateRange} from 'hooks/useHistoryStateDateRange';

interface NonHatchActivityType {
  startTime: string;
  endTime: string;
  comments: string;
  id: string;
  reason: string;
}

export function NonHatchShiftingActivity() {
  const history = useHistory();
  const {engine, eventCreator} = useEngineContext();

  const {activityId, shiploaderId} = useParams<{
    shiploaderId: string;
    activityId: string;
  }>();

  const shiploader = useShiploader(shiploaderId);

  const isEditing = React.useMemo(() => {
    return Boolean(activityId);
  }, [activityId]);

  const vessel = useVessel();
  const reclaimStart = vessel.reclaimStart ?? new Date();
  const validationSchema = yup.object().shape({
    startTime: yup
      .date()
      .required('This field is required')
      .min(
        reclaimStart,
        ` Start time cannot be before vessel start time (${reclaimStart.toDateString()} ${reclaimStart.toLocaleTimeString()})`
      ),
    endTime: yup
      .string()
      .required('This field is required')
      .test(
        'is-greater',
        'End time should be greater than start time',
        function (value) {
          const {startTime} = this.parent;
          return moment(value).isAfter(moment(startTime));
        }
      ),
    reason: yup.string().required('This field is required'),
  });

  const activity = useVesselActivity(activityId);

  const pvEndDate = getLastVesselActivityEndDate(vessel.activities, shiploader);

  const sd = pvEndDate !== '' ? pvEndDate : reclaimStart ?? new Date();

  const {startDate, endDate} = useHistoryStateDateRange(sd);

  const initialValues = React.useMemo(() => {
    if (isEditing && vessel && activity && activity.nonHatchShiftingActivity) {
      return {
        startTime: moment(activity.startDate).format('YYYY-MM-DDTHH:mm'),
        endTime: moment(activity.endDate).format('YYYY-MM-DDTHH:mm'),
        comments: activity.nonHatchShiftingActivity.comments,
        id: activity.id,
        shiploader,
        reason: activity.nonHatchShiftingActivity.reason,
      };
    } else {
      return {
        startTime: moment(startDate).format('YYYY-MM-DDTHH:mm'),
        endTime: moment(endDate).format('YYYY-MM-DDTHH:mm'),
        comments: '',
        id: '',
        shiploader,
        reason: '',
      };
    }
  }, [isEditing, vessel, activity, shiploader, startDate, endDate]);

  return (
    <BaseForm<NonHatchActivityType>
      isEditing={isEditing}
      validationSchema={validationSchema}
      initialValues={initialValues}
      onCreateSubmit={async ({startTime, endTime, comments, reason}) => {
        const event = eventCreator.nonHatchShiftingActivityCreated({
          startDate: moment(startTime).toDate(),
          endDate: moment(endTime).toDate(),
          comments,
          shiploader,
          vesselId: vessel.id,
          reason,
        });

        await engine.process(event);
        history.goBack();
      }}
      onEditSubmit={async ({startTime, endTime, comments, id, reason}) => {
        const event = eventCreator.nonHatchShiftingActivityEdited({
          id,
          startDate: moment(startTime).toDate(),
          endDate: moment(endTime).toDate(),
          comments,
          shiploader,
          vesselId: vessel.id,
          reason,
        });

        await engine.process(event);
        history.goBack();
      }}
    >
      {({values, submitForm}) => {
        return (
          <Box py={2} width="100%">
            <Grid container spacing={3}>
              {isEditing && (
                <Grid item xs={12}>
                  <Box px={1}>
                    <Typography variant="h4" color="primary">
                      Shifting activity
                    </Typography>
                  </Box>
                </Grid>
              )}
              <StartEndDateTime />

              <Grid item xs={6}>
                <Box px={1}>
                  <Duration
                    startTime={values.startTime}
                    endTime={values.endTime}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box px={1}>
                  <Field
                    fullWidth
                    component={TextField}
                    name="reason"
                    label="Reason 1"
                    placeholder="Reason 1"
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box px={1}>
                  <Field
                    fullWidth
                    multiline
                    component={TextField}
                    name="comments"
                    label="Comments"
                    placeholder="Comments"
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <ButtonBar
                  saveText="save"
                  createText="create"
                  isEditing={isEditing}
                  handleSave={submitForm}
                  vessel={vessel}
                  activityId={activityId}
                  title="Delete Shifting Activity"
                  subtitle="Are you sure you want to delete this activity?"
                />
              </Grid>
            </Grid>
          </Box>
        );
      }}
    </BaseForm>
  );
}
